/** @format */

import { Chain, KillSwitches } from '@shared/data-access/interfaces';
import { InjectionToken, Signal, computed, inject } from '@angular/core';

import { Store } from '../app.store';
import { UtilityService } from '../utility.service';
import { toSignal } from '@angular/core/rxjs-interop';

// For type safety
const KILLSWITCH_NAMES = [
  'scorecardPlusEnabled',
  'socialCommerceFlow',
  'scBuyNowEnabled',
  'sc24TermsAndConditions',
  'SC5RewardEnabled',
] as const;

type KillSwitchName = (typeof KILLSWITCH_NAMES)[number];

type KillSwitchSignals = {
  readonly [K in KillSwitchName]: Signal<boolean>;
} & {
  get(name: string): Signal<boolean>;
};

function killSwitchFactory(): KillSwitchSignals {
  const store = inject(Store);
  const chain = inject(Chain);
  const utilityService = inject(UtilityService);

  const killSwitchValues = toSignal(store.selectDistinctKey('ENVIRONMENT_KILLSWITCH_VALUES'), {
    initialValue: {} as KillSwitches,
  });

  const signalCache = new Map<string, Signal<boolean>>();

  const createSignal = (name: string): Signal<boolean> => {
    if (signalCache.has(name)) {
      return signalCache.get(name)!;
    }

    const signal = computed(() =>
      utilityService.checkKillSwitch(name, killSwitchValues(), chain.chainIdentifierAbbr),
    );

    signalCache.set(name, signal);
    return signal;
  };

  const signals = KILLSWITCH_NAMES.reduce(
    (acc, name) => {
      acc[name] = createSignal(name);
      return acc;
    },
    {} as Record<KillSwitchName, Signal<boolean>>,
  );

  return {
    ...signals,
    get: createSignal,
  };
}

export const KILLSWITCHES = new InjectionToken<KillSwitchSignals>('KillSwitchSignals', {
  providedIn: 'root',
  factory: killSwitchFactory,
});

export type { KillSwitchSignals, KillSwitchName };
